export type SortDirection = 'asc' | 'desc' | null;

export type DateRange = {
  start: Date | null;
  end: Date | null;
};

export enum VehicleStatus {
  IDLE = 'IDLE',
  ACTIVE = 'ACTIVE',
  LOST = 'LOST',
  IN_REPAIR = 'IN_REPAIR',
  BROKEN = 'BROKEN',
  RETURNED = 'RETURNED',
}

export enum HandoverStatus {
  ACTIVE = 'ACTIVE',
  RETURNED = 'RETURNED',
  PRE_RETURN = 'PRE_RETURN',
}

export enum ReturnSourceType {
  INCIDENT = 'INCIDENT',
  REPOSSESSION = 'REPOSSESSION',
  MAINTENANCE = 'MAINTENANCE',
}

export enum BusinessUnit {
  QUICK_COMMERCE = 'QUICK_COMMERCE',
  EXPRESS = 'EXPRESS',
  X_DOCK = 'X_DOCK',
  SCHEDULED_INSTANT = 'SCHEDULED_INSTANT',
  CANVASSER_RENTAL = 'CANVASSER_RENTAL',
  FOUR_WHEEL = '4_WHEEL',
  OUTSOURCING = 'OUTSOURCING',
  STAGING = 'STAGING',
  DASH_FLEET = 'DASH_FLEET',
}

export const BUSINESS_UNIT_LABELS: Record<string, string> = {
  [BusinessUnit.QUICK_COMMERCE]: 'Quick Commerce',
  [BusinessUnit.EXPRESS]: 'Express',
  [BusinessUnit.X_DOCK]: 'X Dock',
  [BusinessUnit.SCHEDULED_INSTANT]: 'Scheduled Instant',
  [BusinessUnit.CANVASSER_RENTAL]: 'Canvasser Rental',
  [BusinessUnit.FOUR_WHEEL]: '4 Wheel',
  [BusinessUnit.OUTSOURCING]: 'Outsourcing',
  [BusinessUnit.STAGING]: 'Staging',
  [BusinessUnit.DASH_FLEET]: 'Dash Fleet',
};

export const BUSINESS_UNIT_OPTIONS = Object.values(BusinessUnit).map(
  (businessUnit) => ({
    key: businessUnit,
    value: businessUnit,
    label: BUSINESS_UNIT_LABELS[businessUnit],
  })
);

export const getBusinessUnitLabel = (value?: string | null) => {
  if (!value) return '-';

  return (
    BUSINESS_UNIT_LABELS[value] ||
    value
      .toString()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase())
  );
};

export type Column<T = any> = {
  key: string;
  title: string;
  /** Small pill shown next to the header title, e.g. "NEW". */
  titleBadge?: string;
  width?: string;
  sortable?: boolean;
  render?: (value: any, record: T, index: number) => React.ReactNode;
  align?: 'left' | 'center' | 'right';
  sticky?: 'left' | 'right';
  /**
   * Column customization (see `useTableColumns` + `ColumnSettings`).
   * Pinned columns are always visible, cannot be reordered/hidden by the
   * user, and stay anchored at their defined position. The `actions` column
   * is treated as pinned automatically.
   */
  pinned?: boolean;
  /** Start hidden by default; the user can turn it on via Edit Columns. */
  defaultHidden?: boolean;
};

export type Size = 'small' | 'medium' | 'large';

export type CardColor = 'blue' | 'green' | 'yellow' | 'red' | 'purple';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'danger'
  | 'white'
  | 'neutral';

export type StatusVariant =
  | 'success'
  | 'warning'
  | 'error'
  | 'info'
  | 'neutral'
  | 'pending';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Oem {
  ID: string;
  name: string;
  partnershipStatus: 'ACTIVE' | 'INACTIVE';
  dashboardLink: string;
  dashboardUsername: string;
  dashboardPassword: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface OemListRequest {
  size?: string;
  page?: string;
  partnershipStatuses?: string;
  search?: string;
  isActive?: string;
}

export interface OemListResponse {
  data: Oem[];
  pagination: {
    total: number;
    page: number;
    size: number;
    lastPage: number;
  };
}

export interface Model {
  ID: string;
  oemID: string;
  oemName: string;
  name: string;
  contractStartDate: string;
  contractEndDate: string;
  contractDuration: number;
  unitType: 'NEW' | 'SECOND';
  purchasePrice: number;
  sellingPrice: number;
  contractType: 'RTO' | 'RENT';
  batteryType: 'CHARGING' | 'SWAP' | 'BOTH';
  maxDistancePerBattery: number;
  maxWeight: number;
  deposit: number;
  area: string;
  // Catalog photo (recommended 512x512); null for models created before the
  // photo field existed or when ops hasn't uploaded one yet.
  imageURL?: string | null;
  createdAt?: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;
}

export interface ModelListRequest {
  size?: string;
  page?: string;
  search?: string;
  oemIDs?: string;
  unitType?: string;
  contractType?: string;
  batteryType?: string;
  area?: string;
}

export interface ModelListResponse {
  data: Model[];
  pagination: {
    total: number;
    page: number;
    size: number;
    lastPage: number;
  };
}

// Filter types for Models
export interface ModelFilterOption {
  key: string;
  label: string;
  value: string | boolean;
}

export interface ModelFilterGroup {
  title: string;
  key: string;
  options: ModelFilterOption[];
  multiple?: boolean;
}

// Handover interfaces
export interface Handover {
  ID: string;
  vehicleID: string;
  vehicle: string; // Vehicle plate number
  mitraName: string;
  mitraID?: string;
  clientID: string;
  client: string; // Client name
  pitstopID: string;
  pitstop: string; // Pitstop name
  platNomor: string;
  clientName: string;
  hubId: string;
  hubName: string;
  driverID?: string;
  driverName?: string;
  handoverDate: string;
  modelName: string;
  oemName: string;
  oemID?: string;
  driverCode?: string;
  /**
   * The PLAN, not the actuals. A row waiting to be collected has no
   * `handoverDate` and one waiting to come back no `returnDate`, so on those two
   * rungs this is the only date there is. Null on legacy FMS rows, which are
   * recorded after the handover already happened.
   */
  scheduledHandoverDate?: string | null;
  scheduledReturnDate?: string | null;
  /** Window of the planned PICKUP only — a scheduled return carries no time. */
  slotStartTime?: string | null;
  slotEndTime?: string | null;
  bastLink?: string;
  notes?: string;
  returnUnitEquipment?: string[];
  odometer?: number;
  driverStatus?: string;
  returnDate?: string;
  returnOdometer?: number;
  returnCondition?: 'GOOD' | 'MINOR_DAMAGE' | 'MAJOR_DAMAGE' | string;
  returnReason?: string;
  returnSource?: ReturnSourceType | string;
  returnBastLink?: string;
  returnNotes?: string;
  vehicleBusinessUnit?: string;
  vehicleHubName?: string;
  vehicleReturnImageURL?: string;
  vehicleHandoverImageURL?: string;
  vehicleAndMitraReturnImageURL?: string;
  unitEquipment: string[];
  duration: string; // e.g., "53 Days"
  startDate: string;
  endDate: string;
  status?: HandoverStatus;
  createdAt?: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;
  vehicleProcessType?: string;
  resignType?: string;
  requestDate?: string;
}

export interface HandoverListRequest {
  size?: string;
  page?: string;
  search?: string;
  modelIDs?: string;
  oemIDs?: string;
  statuses?: string;
  isActive?: boolean;
  isAvailable?: string;
  hubIDs?: string;
  clientIDs?: string;
  startDate?: string;
  endDate?: string;
  handoverStartDate?: string;
  handoverEndDate?: string;
  returnStartDate?: string;
  returnEndDate?: string;
  /**
   * The PLANNED dates, one pair per waiting rung. A waiting row has no actual
   * date — `handoverDate` is null before pickup, `returnDate` before the unit is
   * back — so each of those tabs filters its own plan and nothing else.
   */
  scheduledStartDate?: string;
  scheduledEndDate?: string;
  scheduledReturnStartDate?: string;
  scheduledReturnEndDate?: string;
}

export interface HandoverListResponse {
  data: Handover[];
  pagination: {
    total: number;
    page: number;
    size: number;
    lastPage: number;
  };
}

// Repossession status enums
export enum RepossessionStatus {
  OPEN = 'OPEN',
  IN_PROGRESS = 'IN_PROGRESS',
  FOUND = 'FOUND',
  PENDING_APPROVAL = 'PENDING_APPROVAL',
  POTENTIAL_LOSS = 'POTENTIAL_LOSS',
  WRITTEN_OFF = 'WRITTEN_OFF',
  CLOSED = 'CLOSED',
}

export enum RepossessionDecision {
  APPROVE_RECOVERY = 'APPROVE_RECOVERY',
  APPROVE_TOTAL_LOSS = 'APPROVE_TOTAL_LOSS',
  APPROVE_CONTINUE_SEARCH = 'APPROVE_CONTINUE_SEARCH',
  REJECT_CONTINUE_SEARCH = 'REJECT_CONTINUE_SEARCH',
}

export enum RepossessionRecoveryCostType {
  TOWING_FEE = 'TOWING_FEE',
  LEGAL_FEE = 'LEGAL_FEE',
  STORAGE_FEE = 'STORAGE_FEE',
  VEHICLE_DAMAGE = 'VEHICLE_DAMAGE',
  OTHER = 'OTHER',
}

export enum RepossessionSettlementType {
  INSURANCE_CLAIM = 'INSURANCE_CLAIM',
  OEM_LIABILITY = 'OEM_LIABILITY',
  DRIVER_LIABILITY = 'DRIVER_LIABILITY',
  COMPANY_LOSS = 'COMPANY_LOSS',
  OTHER = 'OTHER',
}

export enum RepossessionSeverity {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  CRITICAL = 'CRITICAL',
}

export enum RepossessionOpsRecommendation {
  DECLARE_TOTAL_LOSS = 'DECLARE_TOTAL_LOSS',
  PROCEED_LEGAL_ACTION = 'PROCEED_LEGAL_ACTION',
  FILE_INSURANCE_CLAIM = 'FILE_INSURANCE_CLAIM',
  CONTINUE_SEARCH_WITH_BUDGET = 'CONTINUE_SEARCH_WITH_BUDGET',
}

// Repossession interfaces
export interface Repossession {
  ID: string;
  code: string;
  vehicle: string;
  vehicleID: string;
  platNomor?: string;
  driverCode: string;
  oemName: string;
  client: string;
  clientID: string;
  driverID?: string;
  driverName?: string;
  modelName?: string;
  pitstop: string;
  pitstopID: string;
  clientName?: string;
  hubName?: string;
  status: RepossessionStatus;
  severity: RepossessionSeverity;
  assignedOpsTeam?: string;
  reason?: string;
  createdDate: string;
  lastUpdated: string;
  assignedTo: string;
  picSubmit: string;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
  // New fields for PRD requirements
  inProgressTimestamp?: string;
  recoveryCost?: number;
  costType?: string;
  recoveryNotes?: string;
  searchEfforts?: string;
  escalationReason?: string;
  lastKnownInfo?: string;
  opsRecommendation?: RepossessionOpsRecommendation;
  settlementAmount?: number;
  settlementType?: string;
  settlementNotes?: string;
  financeApproved?: boolean;
  escalationType?: 'RECOVERY_COST' | 'POTENTIAL_LOSS' | 'APPROVE_COST';
  estimatedVehicleValue?: number;
  escalationDueAt?: string;
  escalationTrigger?: 'AUTOMATIC' | 'MANUAL';
}

export interface RepossessionStatusHistory {
  id: string;
  repossessionId: string;
  status: RepossessionStatus;
  createdBy: string;
  createdAt: string;
  reason?: string;
  notes?: string;
  isSystemGenerated?: boolean;
}

export interface RepossessionStatusHistoryApi {
  ID: string;
  repossessionID: string;
  fromStatus?: RepossessionStatus;
  toStatus?: RepossessionStatus;
  reason?: string | null;
  metadata?: {
    recoveryCost?: number;
    recoveryNotes?: string;
    recoveryCostType?: string;
    action?: string;
    decision?: string;
    decisionNotes?: string;
    opsRecommendation?: string;
    estimatedCost?: number;
    searchEfforts?: string;
    escalationReason?: string;
    lastKnownInfo?: string;
    escalationTrigger?: 'AUTOMATIC' | 'MANUAL';
    settlementAmount?: number;
    settlementType?: string;
    settlementNotes?: string;
    settlementDocumentURL?: string;
    financeApproval?: boolean;
    reason?: string;
    recoveryCostTypeOther?: string;
  } | null;
  createdBy?: string;
  createdAt?: string;
}

// Maintenance status enums
export enum MaintenanceStatus {
  OPEN = 'OPEN',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  PENDING_APPROVAL_COMPLETION = 'PENDING_APPROVAL_COMPLETION',
  PENDING_APPROVAL_TEMP_SWAP = 'PENDING_APPROVAL_TEMP_SWAP',
  CLOSED = 'CLOSED',
  TEMP_SWAP = 'TEMP_SWAP',
  PENDING_APPROVAL = 'PENDING_APPROVAL',
  PERM_SWAP = 'PERM_SWAP',
  PENDING_APPROVAL_PERM_SWAP = 'PENDING_APPROVAL_PERM_SWAP',
}

export enum MaintenanceSeverity {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  CRITICAL = 'CRITICAL',
}

export enum MaintenanceCategory {
  BATTERY_REPLACEMENT = 'BATTERY_REPLACEMENT',
  MOTOR_BLDC_REPAIR = 'MOTOR_BLDC_REPAIR',
  TIRE_CHANGE = 'TIRE_CHANGE',
  BRAKE_SYSTEM = 'BRAKE_SYSTEM',
  BODY_REPAIR = 'BODY_REPAIR',
  ELECTRICAL_SYSTEM = 'ELECTRICAL_SYSTEM',
  FRAME_DAMAGE = 'FRAME_DAMAGE',
  GENERAL_MAINTENANCE = 'GENERAL_MAINTENANCE',
  OTHER = 'OTHER',
}

export enum MaintenanceReason {
  HUB_AUDIT = 'HUB_AUDIT',
  SCHEDULED_MAINTENANCE = 'SCHEDULED_MAINTENANCE',
  LINKED_TO_INCIDENT = 'LINKED_TO_INCIDENT',
  MITRA_REPORT = 'MITRA_REPORT',
  POST_HANDOVER_INSPECTION = 'POST_HANDOVER_INSPECTION',
}

// Maintenance interfaces
export interface Maintenance {
  ID: string;
  code: string;
  reason: string;
  issueID?: string;
  incidentID?: string;
  vehicleID: string;
  vehiclePlatNomor: string;
  vehicleOemName: string;
  vehicleModelName: string;
  driverID: string;
  driverCode: string;
  driverName: string;
  categories: string[];
  clientID: number;
  clientName: string;
  oemID: string;
  oemName: string;
  mitraName: string;
  issueDescription: string;
  maintenanceDate: string;
  severity: string;
  estimatedCost: number;
  costResponsibility: string;
  oemServiceCenter: string;
  expectedCompletionDate: string;
  vehicleImageURL: string;
  notes: string;
  status: string;
  createdAt: string;
  createdBy: string;
  updatedAt: string | null;
  updatedBy: string | null;
}
// Issue status enums
export enum IssueStatus {
  SUBMITTED = 'SUBMITTED',
  IN_PROGRESS = 'IN_PROGRESS',
  RESOLVED = 'RESOLVED',
  CLOSED = 'CLOSED',
}

export enum IssueSeverity {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  CRITICAL = 'CRITICAL',
}

// Issue interface
export interface Issue {
  ID: string;
  code: string;
  vehiclePlatNomor: string;
  vehicleModelName: string;
  mitraName: string;
  mitraCode: string;
  driverName?: string;
  driverCode?: string;
  driverID?: string;
  imageURL?: string;
  notes?: string;
  issueType: string;
  severity: IssueSeverity;
  status: IssueStatus;
  createdAt: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;
  pic: string;
  costBearer?: string;
  estimatedCost?: number;
  clientName?: string;
  vehicleOemName?: string;
  vehicleID?: string;
  oemID?: string;
  clientID?: number;
  incidentDate?: string;
  linkedMaintenanceCode?: string;
  linkedMaintenanceID?: string;
}

export interface IssueListRequest {
  page?: string | number;
  size?: string | number;
  severities?: string;
  statuses?: string;
  search?: string;
  issueTypes?: string;
  oemIDs?: string;
  modelIDs?: string;
  startDate?: string;
  endDate?: string;
  reason?: string;
  costResponsibilities?: string;
}

export interface IssueListResponse {
  data: Issue[];
  pagination: {
    total: number;
    page: number;
    size: number;
    lastPage: number;
  };
}
